<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import PaymentEditDialog from '@/domains/billing/PaymentEditDialog.vue';
import Unauthorized from '@/domains/errors/Unauthorized.vue';
import { viewportSize } from '@/platform/host-sdk/sub-broadcaster';
import { usePermission } from '@/platform/identity/use-permission';
import { routePermitted, useChrome } from '@/platform/routing/chrome';
import { captureVueError } from '@/platform/telemetry/frontend-alert';
import ErrorBoundary from '@/shared/ui/ErrorBoundary.vue';
import { useResizeObserver } from '@vueuse/core';

import AppBanner from './AppBanner.vue';
import Sidebar from './Sidebar.vue';
import TrialDialog from './TrialDialog.vue';

const { t } = useI18n();
const chrome = useChrome();

// In-place permission gate: a route the user lacks perms for renders the
// permission-denied page in the content area with the URL and chrome intact,
// matching legacy's 401 component swap. Reactive on the perm snapshot, so a
// live permission change (WS push / per-nav refresh) swaps the view in both
// directions without a navigation.
const route = useRoute();
const { hasPerm } = usePermission();
const denied = computed(() => !routePermitted(route.meta, hasPerm));

const contentArea = ref<HTMLElement | null>(null);

// A render crash in the routed view is contained here so the chrome (sidebar /
// banner) survives; the boundary hands the error over for telemetry since
// shared/ can't reach platform/telemetry itself.
function onViewCrash(err: unknown, info: string): void {
  captureVueError(err, null, info);
}

// Source-level dedupe: ResizeObserver fires on every layout tick during a
// window drag and on every observed mutation. Skipping no-op writes here
// stops the broadcaster watch from running and the wujie bus from carrying
// redundant frames. The broadcaster's signature cache catches the rest, but
// cutting at the source is cheaper.
let lastH = 0;

useResizeObserver(contentArea, (entries) => {
  // floor, not round — half-pixel rounding up overshoots the host viewport
  // and surfaces a double scrollbar inside the wujie sandbox.
  // (See reference_viewport_sync_subpixel_scrollbar.md memory.)
  const h = Math.floor(entries[0].contentRect.height);
  if (h > 0 && h !== lastH) {
    lastH = h;
    viewportSize.value = { height: h };
  }
});
</script>

<template>
  <div data-testid="app-layout" class="bg-surface text-fg flex h-screen w-screen">
    <a href="#main-content" class="skip-link">{{ t('common.skipToMain') }}</a>
    <!-- Gate the chrome at the mount point, not just inside each child's
         template. Sidebar / AppBanner / TrialDialog call useUnreadNotifications()
         / account-info queries at setup() — mounting them on an authFree
         `layout: 'minimal'` route (e.g. /401) issues authenticated requests
         on a page the user reached precisely because they're unauthorised. -->
    <Sidebar v-if="chrome.sidebar" />
    <div class="flex min-w-0 flex-1 flex-col">
      <!-- AppBanner hosts both the account-state banner and the flashbar;
           the flashbar is gated separately inside on chrome.flashbar. -->
      <AppBanner v-if="chrome.trialBanner || chrome.flashbar" />
      <main id="main-content" ref="contentArea" tabindex="-1" class="min-h-0 flex-1">
        <ErrorBoundary :reset-key="route.fullPath" @error="onViewCrash">
          <div v-if="denied" class="flex h-full items-center justify-center">
            <Unauthorized />
          </div>
          <RouterView v-else />
        </ErrorBoundary>
      </main>
    </div>
    <TrialDialog v-if="chrome.trialBanner" />
    <!-- Always mounted. usePaymentEditDialog is a singleton; unmounting
         while `isOpen=true` would lose Stripe Elements on the next
         remount because the isOpen watcher isn't immediate. -->
    <PaymentEditDialog />
  </div>
</template>

<style scoped>
.skip-link {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: var(--z-index-skip-link);
  padding: 4px 8px;
  background: #ffffff;
  color: var(--color-brand-azure, #008ff4);
  border: 1px solid currentColor;
  border-radius: 4px;
  font-size: 13px;
  text-decoration: none;
}
.skip-link:focus {
  left: 8px;
  top: 6px;
}
</style>
